import React, { useState, useEffect } from 'react';
import Card from './Card';

interface GachaMaruAnimationProps {
  isOpen: boolean;
  onComplete: (result: { itemId?: string; itemName: string; imageUrl?: string; isBlank?: boolean }) => void;
  result: { itemId?: string; itemName: string; imageUrl?: string; isBlank?: boolean };
}

const GachaMaruAnimation: React.FC<GachaMaruAnimationProps> = ({
  isOpen,
  onComplete,
  result
}) => {
  const [tapCount, setTapCount] = useState(0);
  const [requiredTaps] = useState(Math.floor(Math.random() * 5) + 3); // 3-7번
  const [isBreaking, setIsBreaking] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [cracks, setCracks] = useState<Array<{ x: number; y: number; rotation: number }>>([]);

  const handleTap = () => {
    if (isBreaking || showResult) return;

    const newTapCount = tapCount + 1;
    setTapCount(newTapCount);

    // 새로운 균열 추가
    const newCrack = {
      x: Math.random() * 100,
      y: Math.random() * 100,
      rotation: Math.random() * 360
    };
    setCracks([...cracks, newCrack]);

    if (newTapCount >= requiredTaps) {
      setIsBreaking(true);
      setTimeout(() => {
        setShowResult(true);
        setTimeout(() => {
          onComplete(result);
        }, 1000);
      }, 500);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setTapCount(0);
      setIsBreaking(false);
      setShowResult(false);
      setCracks([]);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      fontFamily: 'Pretendard'
    }}>
      <Card
        width="500px"
        padding="32px"
        borderRadius="16px"
        background="white"
        boxShadow="0px 8px 16px rgba(0, 0, 0, 0.12)"
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          alignItems: 'center'
        }}>
          <h2 style={{
            margin: 0,
            color: 'var(--Color-text-normal, #161616)',
            fontSize: '24px',
            fontFamily: 'pretendard',
            fontWeight: '600'
          }}>
            MARU 로고 뽑기
          </h2>
          
          <div
            style={{
              width: '300px',
              height: '300px',
              position: 'relative',
              cursor: isBreaking || showResult ? 'default' : 'pointer',
              userSelect: 'none'
            }}
            onClick={handleTap}
          >
            {/* MARU 로고 */}
            {!showResult && (
              <div style={{
                width: '100%',
                height: '100%',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #FF6F0F 0%, #FF8E3C 100%)',
                borderRadius: '12px',
                border: '2px solid var(--Color-line-normal, #F0F0F0)',
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                transform: isBreaking ? 'scale(0.95)' : 'scale(1)',
                transition: 'transform 0.1s',
                opacity: isBreaking ? 0.7 : 1
              }}>
                <div style={{
                  color: 'white',
                  fontSize: '80px',
                  fontFamily: 'pretendard',
                  fontWeight: '900',
                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.2)'
                }}>
                  MARU
                </div>
                
                {/* 균열 표시 */}
                {cracks.map((crack, index) => (
                  <div
                    key={index}
                    style={{
                      position: 'absolute',
                      left: `${crack.x}%`,
                      top: `${crack.y}%`,
                      width: '40px',
                      height: '2px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      transform: `rotate(${crack.rotation}deg)`,
                      transformOrigin: 'center'
                    }}
                  />
                ))}
              </div>
            )}

            {/* 결과 표시 */}
            {showResult && (
              <div style={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                borderRadius: '12px',
                background: result.isBlank ? '#FFEBEE' : '#E8F5E9',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '16px',
                border: '2px solid var(--Color-line-normal, #F0F0F0)',
                animation: 'fadeIn 0.5s'
              }}>
                {result.isBlank ? (
                  <>
                    <span style={{ fontSize: '80px' }}>❌</span>
                    <div style={{
                      color: '#C62828',
                      fontSize: '32px',
                      fontFamily: 'pretendard',
                      fontWeight: '600'
                    }}>
                      꽝
                    </div>
                  </>
                ) : (
                  <>
                    {result.imageUrl ? (
                      <img 
                        src={result.imageUrl} 
                        alt={result.itemName}
                        style={{
                          width: '150px',
                          height: '150px',
                          objectFit: 'cover',
                          borderRadius: '8px'
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: '80px' }}>📦</span>
                    )}
                    <div style={{
                      color: '#2E7D32',
                      fontSize: '24px',
                      fontFamily: 'pretendard',
                      fontWeight: '600',
                      textAlign: 'center',
                      padding: '0 16px'
                    }}>
                      {result.itemName}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {!showResult && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              alignItems: 'center'
            }}>
              <div style={{
                color: 'var(--Color-text-assistive, #989898)',
                fontSize: '14px',
                fontFamily: 'pretendard',
                fontWeight: '400',
                textAlign: 'center'
              }}>
                MARU 로고를 탭하세요
              </div>
              <div style={{
                color: 'var(--Color-text-normal, #161616)',
                fontSize: '16px',
                fontFamily: 'pretendard',
                fontWeight: '600',
                textAlign: 'center'
              }}>
                {tapCount} / {requiredTaps}회
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default GachaMaruAnimation;




